"use client";

// src/components/posts/MetroFeed.tsx
//
// Unified metro feed for /posts/{metro} (shape brief confirmed 2026-07-11).
// The hub used to open on a 33-way category directory with no counts, where
// most first clicks landed on empty categories. It now opens on one
// newest-first feed of the metro's actual posts with bucket filter chips;
// the category directory folds in below (still SSR'd for crawl equity).
//
// Data: /api/posts/feed?locationSlug= — SSR'd by the server page with the
// viewer's token (guests get blurred/scrubbed rows from the API; NO
// client-side gating here). When the metro is thin, a labeled
// "From other cities" section fills in honestly — never passed off as local.

import React from 'react';
import PostCard, { PostCardProps } from '@/components/PostCard';
import PostCardSkeleton from '@/components/skeletons/PostCard';
import { apiFetch } from '@/lib/api';
import { bucketForCategory, BUCKET_LABELS, type BucketKey } from '@/lib/posts/labels';

const THIN_METRO_THRESHOLD = 3;

/** B3: how long Retry waits for the featured list before it shows the feed without it. */
export const FEATURED_RETRY_TIMEOUT_MS = 3000;

type Chip = { value: 'all' | BucketKey; label: string };

// Order puts the two job-side buckets together: a producer hiring, then the
// people advertising themselves for that work. Labels come from BUCKET_LABELS
// so a chip cannot disagree with the ribbon on the card underneath it.
const CHIPS: Chip[] = [
  { value: 'all', label: 'All' },
  { value: 'hiring', label: BUCKET_LABELS.hiring },
  { value: 'looking', label: BUCKET_LABELS.looking },
  { value: 'connect', label: BUCKET_LABELS.connect },
  { value: 'services', label: BUCKET_LABELS.services },
];

/** Chip values that are real buckets, i.e. everything except the "all" pseudo-chip. */
const BUCKET_CHIP_VALUES = CHIPS.map(c => c.value).filter((v): v is BucketKey => v !== 'all');

function mapPost(p: any): PostCardProps {
  return {
    ...p,
    unsafe: (p.isUnsafeContent ?? p.IsUnsafeContent ?? p.unsafe) !== false,
    createdAt: p.createdAt ?? p.CreatedAt ?? p.datePosted ?? p.DatePosted ?? p.lastModified ?? p.LastModified ?? null,
  };
}

function postBucket(p: PostCardProps): BucketKey | null {
  return bucketForCategory(p.categorySlug || p.categoryDescription);
}

// B3: featured casting calls from /api/posts/featured, once each. null or a failed fetch
// gives an empty list, and an empty list renders the feed exactly as it is without them.
function mapFeatured(list: any[] | null | undefined): PostCardProps[] {
  const seen = new Set<number>();
  const out: PostCardProps[] = [];
  for (const raw of Array.isArray(list) ? list : []) {
    const p = mapPost(raw);
    if (seen.has(p.postId)) continue;
    seen.add(p.postId);
    out.push(p);
  }
  return out;
}

export default function MetroFeed({
  location,
  placeName,
  initialFeed,
  featured = null,
}: {
  location: string;
  placeName: string;
  /** SSR'd metro feed (viewer-correct). null = the server fetch failed. */
  initialFeed: PostCardProps[] | null;
  /**
   * B3: SSR'd featured casting calls (viewer-correct, at most two). They render first, with
   * the Sponsored label, in the All and Now Hiring views only; the organic list and the
   * "From other cities" fill drop their ids, so no post shows twice. null = the fetch failed.
   */
  featured?: PostCardProps[] | null;
}) {
  const [posts, setPosts] = React.useState<PostCardProps[] | null>(
    initialFeed ? initialFeed.map(mapPost) : null
  );
  const [featuredPosts, setFeaturedPosts] = React.useState<PostCardProps[]>(() => mapFeatured(featured));
  const [failed, setFailed] = React.useState(initialFeed == null);
  const [loading, setLoading] = React.useState(false);
  const [national, setNational] = React.useState<PostCardProps[] | null>(null);
  const [bucket, setBucket] = React.useState<'all' | BucketKey>('all');

  // Initial bucket from ?bucket= (read on mount; avoids the useSearchParams
  // Suspense requirement on this client island).
  React.useEffect(() => {
    try {
      const b = new URLSearchParams(window.location.search).get('bucket');
      // Validated against the chip list, not a hardcoded set: a fifth bucket
      // would otherwise be silently ignored here and land everyone on All.
      const known = BUCKET_CHIP_VALUES.find(v => v === b);
      if (known) setBucket(known);
    } catch {}
  }, []);

  const refetch = React.useCallback(async () => {
    setLoading(true);
    setFailed(false);
    // Retry refetches both. A failed featured fetch only empties the featured list; the feed
    // then renders exactly as it does without featured calls. The featured wait is bounded
    // (FEATURED_RETRY_TIMEOUT_MS), so a slow or hanging featured endpoint cannot hold the feed
    // behind the skeleton: past the bound the feed renders with no featured calls.
    let featuredTimer: ReturnType<typeof setTimeout> | undefined;
    const featuredLoad = Promise.race<PostCardProps[]>([
      (async () => {
        try {
          const res = await apiFetch(
            `/api/proxy/api/posts/featured?locationSlug=${encodeURIComponent(location)}`,
            { method: 'GET', cache: 'no-store', signal: AbortSignal.timeout(FEATURED_RETRY_TIMEOUT_MS) }
          );
          if (!res.ok) return [];
          return mapFeatured(await res.json());
        } catch {
          return [];
        }
      })(),
      new Promise<PostCardProps[]>(resolve => {
        featuredTimer = setTimeout(() => resolve([]), FEATURED_RETRY_TIMEOUT_MS);
      }),
    ]).finally(() => clearTimeout(featuredTimer));
    try {
      const res = await apiFetch(
        `/api/proxy/api/posts/feed?locationSlug=${encodeURIComponent(location)}&limit=30`,
        { method: 'GET', cache: 'no-store' }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setPosts((Array.isArray(j) ? j : []).map(mapPost));
    } catch {
      setFailed(true);
    } finally {
      setFeaturedPosts(await featuredLoad);
      setLoading(false);
    }
  }, [location]);

  // Thin metro: fill in with a clearly-labeled national section.
  React.useEffect(() => {
    if (posts == null || posts.length >= THIN_METRO_THRESHOLD) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/proxy/api/posts/feed?limit=12', { method: 'GET', cache: 'no-store' });
        if (!res.ok) return;
        const j = await res.json();
        if (cancelled) return;
        const localIds = new Set((posts || []).map(p => p.postId));
        setNational(
          (Array.isArray(j) ? j : [])
            .map(mapPost)
            .filter(p => !localIds.has(p.postId))
            .slice(0, 9)
        );
      } catch { /* the section simply doesn't render */ }
    })();
    return () => { cancelled = true; };
  }, [posts]);

  const switchBucket = (value: 'all' | BucketKey) => {
    setBucket(value);
    // history.replaceState, not router.replace: this route is force-dynamic,
    // so router.replace re-fetches RSC payload and hard-fails offline (same
    // failure mode reproduced on leaderboards, 07-11). Filtering is client-
    // side; only the address bar needs updating.
    try {
      const qs = value === 'all' ? '' : `?bucket=${value}`;
      window.history.replaceState(null, '', `/posts/${location}${qs}`);
    } catch {}
  };

  // B3: featured calls are casting calls, so they belong to All and Now Hiring only. The
  // organic list and the other-cities fill drop their ids, and every count is of distinct posts.
  const featuredIds = new Set(featuredPosts.map(p => p.postId));
  const organic = posts == null ? null : posts.filter(p => !featuredIds.has(p.postId));
  const showFeatured = bucket === 'all' || bucket === 'hiring';
  const visibleFeatured = organic == null || !showFeatured ? [] : featuredPosts;
  const nationalVisible = national == null ? null : national.filter(p => !featuredIds.has(p.postId));
  const totalCount = organic == null ? 0 : organic.length + featuredPosts.length;

  const countFor = (value: 'all' | BucketKey) =>
    organic == null
      ? null
      : value === 'all'
        ? totalCount
        : organic.filter(p => postBucket(p) === value).length + (value === 'hiring' ? featuredPosts.length : 0);

  const visible = organic == null ? null : bucket === 'all' ? organic : organic.filter(p => postBucket(p) === bucket);

  return (
    <section className="mb-8" aria-label={`Latest posts in ${placeName}`}>
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="juicy-rule mb-2" aria-hidden />
          <h2 className="font-serif text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            Latest in {placeName}
          </h2>
        </div>
        {organic != null && totalCount > 0 && (
          <span className="text-sm text-muted-foreground">
            {totalCount === 1 ? '1 post' : `${totalCount} posts`}
          </span>
        )}
      </div>

      {/* Bucket chips — same spec as the leaderboard pills. */}
      <div className="mb-4 flex flex-wrap items-center gap-2" role="group" aria-label="Filter posts by type">
        {CHIPS.map(c => {
          const n = countFor(c.value);
          return (
            <button
              key={c.value}
              type="button"
              onClick={() => switchBucket(c.value)}
              aria-pressed={bucket === c.value}
              className={
                'inline-flex items-center gap-1.5 rounded-full px-3.5 h-11 md:h-8 text-sm transition-colors ' +
                (bucket === c.value
                  ? 'bg-primary text-primary-foreground font-semibold shadow-sm'
                  : 'border border-border text-muted-foreground hover:bg-accent/40 hover:text-foreground')
              }
            >
              {c.label}
              {n != null && n > 0 && c.value !== 'all' && (
                <span className={bucket === c.value ? 'text-primary-foreground/80' : 'text-muted-foreground/70'}>
                  {n}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading || posts == null && !failed ? (
        <div className="grid gap-4 grid-cols-1" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <PostCardSkeleton key={i} variant="light" showMeta />
          ))}
        </div>
      ) : failed ? (
        <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground flex items-center justify-between gap-3">
          <span>Couldn&apos;t load posts.</span>
          <button
            type="button"
            onClick={refetch}
            className="rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-accent/40"
          >
            Retry
          </button>
        </div>
      ) : (visible && visible.length > 0) || visibleFeatured.length > 0 ? (
        <div className="grid gap-4 grid-cols-1">
          {visibleFeatured.map(p => (
            <PostCard key={`sp-${p.postId}`} {...p} sponsored placementToken={p.placementToken} showMeta variant="light" showCategoryRibbon />
          ))}
          {(visible || []).map(p => (
            <PostCard key={p.postId} {...p} showMeta variant="light" showCategoryRibbon />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <p className="text-sm font-medium text-foreground">
            {bucket === 'all'
              ? `No posts in ${placeName} yet.`
              : `No ${BUCKET_LABELS[bucket as BucketKey].toLowerCase()} posts in ${placeName} yet.`}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Be the first: create a post and reach {placeName} members.
          </p>
        </div>
      )}

      {/* Honest cross-metro fill for thin boards — labeled, never "nearby". */}
      {nationalVisible && nationalVisible.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-4 font-serif text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            From other cities
          </h3>
          <div className="grid gap-4 grid-cols-1">
            {nationalVisible.map(p => (
              <PostCard key={`nat-${p.postId}`} {...p} showMeta variant="light" showCategoryRibbon />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
